"use client";

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const FounderStorySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section id="founder-story" ref={sectionRef} className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">OUR STORY

          </span>
          <h2 className="font-jakarta font-bold text-4xl md:text-5xl text-foreground mb-6 tracking-tight">A way to be with them, even after you are gone...

          </h2>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Founder Photo */}
          <div
            className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`
            }>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl transform rotate-3" />
              <AppImage
                src="/images/IMG-20220109-WA0013-1770323559409.jpg"
                alt="Portrait of David Chen, founder of Legacy, man with warm smile in business casual attire"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover" />
              
              <div className="absolute -bottom-6 -right-6 bg-card rounded-2xl p-6 shadow-xl border border-border">
                <div className="flex items-center gap-3">
                  <Icon name="SparklesIcon" size={32} className="text-primary" />
                  <div>
                    <p className="font-jakarta font-bold text-foreground">Founder Mark Hamilton</p>
                    <p className="text-muted-foreground text-sm">... and family</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Story Content */}
          <div
            className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`
            }
            style={{ transitionDelay: '200ms' }}>
            
            <div className="space-y-6">
              <p className="text-muted-foreground text-lg leading-relaxed">You've got a story that your friends and family would love to hear. Why not share it in your own words, whether written or spoken? Give them a glimpse into those meaningful moments that shaped you, turning your memories into a shared legacy that the people you care about can cherish.

              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">This is a chance to take a quiet moment and share something meaningful with someone who matters to you. You can choose your own words, your own timing, and even decide for it to be delivered after you&apos;re gone. It might be a message for a celebration you wish you could attend, a special occasion, or a milestone in someone&apos;s life—like a wedding, a birthday, or a graduation. It&apos;s a way to make sure your love and presence are still felt, no matter what. You can even write your own story, add chapters and pictures, include favorite songs and have it all put together in a personal page and video. Here is the chance to share your Legacy.

              </p>

              {/* Signature */}
              <div className="pt-8 border-t border-border">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="PencilSquareIcon" size={28} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-crimson text-2xl text-foreground italic">Founder Mark Hamilton</p>
                    <p className="text-muted-foreground text-sm">January 2021</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default FounderStorySection;